import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon, MinusIcon } from "lucide-react";

import { cn } from "@bcc/lib/utils";
import { setCheckboxTestId } from "./checkbox.utils";

interface CheckboxProps extends Omit<React.ComponentProps<typeof CheckboxPrimitive.Root>, "checked"> {
  /**
   * The checked state of the checkbox.
   * Can be true, false, or "indeterminate" for a partially selected state.
   */
  checked?: boolean | "indeterminate";
  /**
   * Shorthand for setting checked="indeterminate".
   * If true and checked is not explicitly set, the checkbox will show indeterminate state.
   */
  indeterminate?: boolean;
  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

function Checkbox({ className, checked, indeterminate, testId, name, id, ...props }: CheckboxProps) {
  // Determine the actual checked state
  // indeterminate prop takes precedence if checked is not explicitly "indeterminate"
  const checkedState = React.useMemo(() => {
    if (checked === "indeterminate") return "indeterminate";
    if (indeterminate && checked !== true && checked !== false) {
      return "indeterminate";
    }
    if (indeterminate && checked === false) return "indeterminate";
    return checked;
  }, [checked, indeterminate]);

  const finalTestId = testId ?? setCheckboxTestId(name ?? id ?? "checkbox");

  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      data-testid={finalTestId}
      checked={checkedState}
      name={name}
      id={id}
      className={cn(
        "peer border-primary dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=indeterminate]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 disabled:data-[state=indeterminate]:bg-muted disabled:data-[state=indeterminate]:border-muted-foreground/50 disabled:data-[state=indeterminate]:text-muted-foreground disabled:data-[state=indeterminate]:opacity-100",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator data-slot="checkbox-indicator" className="flex items-center justify-center text-current transition-none">
        {checkedState === "indeterminate" ? <MinusIcon className="size-3.5" /> : <CheckIcon className="size-3.5" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
export type { CheckboxProps };
